import { Publisher } from './api.js';
import { setupEditor } from './editor-core.js';
import { PendingImages, buildPublishContent, markdownToExcerpt } from './content.js';
import { importFile } from './import.js';
import { fileToWebP } from './image.js';

function $id(id) {
  return document.getElementById(id);
}

function setStatus(msg, kind = '') {
  const el = $id('status');
  el.textContent = msg || '';
  el.className = `status ${kind}`.trim();
}

function toast(msg, kind = 'error') {
  const el = $id('toast');
  el.textContent = msg;
  el.className = `toast ${kind}`;
  clearTimeout(el._t);
  el._t = setTimeout(() => (el.className = 'toast hidden'), 4000);
}

async function readFeaturedFile(file) {
  if (!file) return null;
  const blob = await fileToWebP(file, { maxDim: 1200, quality: 0.8 });
  return new Blob([blob], { type: 'image/webp' });
}

export async function boot({ editSlug }) {
  const publisher = new Publisher();
  const pendingImages = new PendingImages();

  const loginView = $id('login-view');
  const editorView = $id('editor-view');
  const logoutBtn = $id('btn-logout');

  const showEditor = () => {
    loginView.hidden = true;
    editorView.hidden = false;
    logoutBtn.hidden = false;
  };
  const showLogin = () => {
    loginView.hidden = false;
    editorView.hidden = true;
    logoutBtn.hidden = true;
  };

  const titleInput = $id('post-title');
  const hashtagsInput = $id('post-hashtags');
  const featuredInput = $id('featured-input');
  const featuredPreview = $id('featured-preview');
  const saveBtn = $id('btn-save');
  const publishBtn = $id('btn-publish');
  const socialPreview = $id('social-preview');

  const core = setupEditor({
    container: $id('editor-container'),
    pendingImages,
    onImport: (file, editor) => importFile(file, {
      editor,
      pendingImages,
      onStatus: setStatus,
      onImported: ({ markdown, title }) => {
        editor.setMarkdown(markdown);
        if (title && !titleInput.value.trim()) titleInput.value = title;
        renderSocialPreview();
      },
    }),
    onChange: () => renderSocialPreview(),
  });

  let currentSlug = editSlug || null;

  const renderSocialPreview = () => {
    const markdown = core.getMarkdown();
    const title = titleInput.value.trim();
    const message = $id('publish-message').value.trim();
    const preview = $id('preview-excerpt');
    const img = $id('preview-image');
    preview.textContent = markdownToExcerpt(markdown);
    const hasFeatured = featuredPreview.src && !featuredPreview.hidden;
    img.hidden = !hasFeatured;
    if (hasFeatured) img.src = featuredPreview.src;
    $id('preview-title').textContent = message || title || 'Vista previa del mensaje';
  };

  featuredInput.addEventListener('change', async () => {
    const f = featuredInput.files[0];
    if (!f) return;
    const blob = await fileToWebP(f, { maxDim: 1200, quality: 0.8 });
    featuredPreview.src = URL.createObjectURL(blob);
    featuredPreview.hidden = false;
    renderSocialPreview();
  });

  const getFeatured = async () => {
    const f = featuredInput.files[0];
    if (!f) return null;
    const blob = await readFeaturedFile(f);
    return new File([blob], 'featured.webp', { type: 'image/webp' });
  };

  $id('publish-message').addEventListener('input', renderSocialPreview);
  $id('post-title').addEventListener('input', renderSocialPreview);
  hashtagsInput.addEventListener('input', renderSocialPreview);

  $id('login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    try {
      await publisher.login($id('login-password').value);
      $id('login-password').value = '';
      setStatus('Sesión iniciada', 'ok');
      showEditor();
      if (editSlug) await loadPost(editSlug);
    } catch (err) {
      toast(err.message);
    }
  });

  logoutBtn.addEventListener('click', () => {
    publisher.logout();
    currentSlug = null;
    showLogin();
  });

  async function loadPost(slug) {
    try {
      const post = await publisher.loadPost(slug);
      titleInput.value = post.title || '';
      hashtagsInput.value = '';
      core.setMarkdown(post.content || '');
      core.setFontFamily(post.font_family || '');
      if (post.image_url) {
        featuredPreview.src = post.image_url;
        featuredPreview.hidden = false;
        featuredPreview.dataset.existing = post.image_url;
      }
      currentSlug = slug;
      renderSocialPreview();
    } catch (err) {
      toast(err.message);
    }
  }

  saveBtn.addEventListener('click', async () => {
    const title = titleInput.value.trim();
    if (!title) {
      toast('Escribe un título');
      return;
    }
    const markdown = core.getMarkdown();
    if (!markdown.trim()) {
      toast('El artículo está vacío');
      return;
    }
    try {
      saveBtn.disabled = true;
      setStatus('Guardando…');
      const content = buildPublishContent(markdown, pendingImages.items);
      const images = pendingImages.items
        .filter((i) => i.inContent)
        .map((i) => ({ name: i.name, file: i.file }));
      const featured = await getFeatured();
      const res = await publisher.savePost({
        slug: currentSlug,
        title,
        content,
        hashtags: hashtagsInput.value.trim(),
        fontFamily: core.getFontFamily(),
        featured,
        images,
      });
      currentSlug = res.slug;
      setStatus(`Guardado → ${res.public_url}`, 'ok');
      toast('Artículo guardado', 'ok');
      renderSocialPreview();
    } catch (err) {
      toast(err.message);
    } finally {
      saveBtn.disabled = false;
    }
  });

  publishBtn.addEventListener('click', async () => {
    if (!currentSlug) {
      toast('Guarda el artículo primero');
      return;
    }
    const platforms = [];
    if ($id('publish-bsky').checked) platforms.push('bluesky');
    if (!platforms.length) {
      toast('Selecciona al menos una plataforma');
      return;
    }
    try {
      publishBtn.disabled = true;
      setStatus('Publicando…');
      const message = $id('publish-message').value.trim();
      const res = await publisher.publish({ slug: currentSlug, platforms, message });
      setStatus('Publicado en ' + (res.published || []).join(', ') || 'Publicado', 'ok');
      toast('Publicado', 'ok');
    } catch (err) {
      toast(err.message);
    } finally {
      publishBtn.disabled = false;
    }
  });

  if (publisher.loggedIn) {
    showEditor();
    if (editSlug) await loadPost(editSlug);
  } else {
    showLogin();
    if (editSlug) setStatus('Inicia sesión para editar');
  }

  renderSocialPreview();
}
